import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { DataChangedBanner } from '@/components/DataChangedBanner';
import { InfoTooltip } from '@/components/InfoTooltip';
import { cn } from '@/lib/utils';
import type { ApiResponse } from '@/lib/api-contract';
import { newerVersion, staleChangeOf, type StaleChange } from '@/lib/stale-write';
import { TASK_SPEC_MAX, type TaskBoard, type TaskDetail, type TaskUpdateInput, type TaskUpdateResponse } from '@shared/types/tasks';
import { MentionText } from './MentionText';
import { LinksOpenInDialogs } from '@/pages/link-dialog/link-scope';
import { useFold } from './use-fold';

/** `base` is the spec's version the edit started from; null before the task has loaded. */
type Draft = { text: string; base: string | null };

/**
 * A task's spec (T-59): the expanded spec a feedback run writes, kept beside the description so the
 * task's own title and description (a mirrored task's are its source's) are never overwritten. Shown as
 * markdown under the description; press it to edit (plain Markdown, no mentions), saves when you click
 * away, Esc discards. With no spec there's only "Add a spec", so the section doesn't take room on every task.
 *
 * Each save sends the version the edit started from: a spec someone (or a run) saved since is refused,
 * the draft stays, and DataChangedBanner offers Reload. A run's own spec always replaces the current one.
 */
export function TaskSpec({
  task,
  hydrated,
  members,
  canEdit,
  saveSpec,
  onReload,
  onNavigate,
}: {
  task: Pick<TaskDetail, 'id' | 'spec' | 'spec_updated_at' | 'contexts'>;
  hydrated: boolean;
  members: TaskBoard['members'];
  canEdit: boolean;
  saveSpec: (input: TaskUpdateInput, optimistic?: Partial<TaskDetail>) => Promise<ApiResponse<TaskUpdateResponse>>;
  /** Resolves true once their version has arrived. */
  onReload: () => Promise<boolean>;
  onNavigate?: () => void;
}) {
  const [draft, setDraft] = useState<Draft | null>(null);
  // This tab's own last save: its version may land before the task's refetch does.
  const [ownVersion, setOwnVersion] = useState<string | null>(null);
  // This tab's last spec save still on its way: the version it started from and the one it made (null
  // if refused). A save sent meanwhile goes on the version that one made, as TaskDescription's do.
  const lastSave = useRef<Promise<{ from: string | null; to: string | null }> | null>(null);
  const version = newerVersion(task.spec_updated_at, ownVersion);
  const [stale, setStale] = useState<StaleChange | null>(null);
  const [reloading, setReloading] = useState(false);
  const { foldRef, foldProps, foldable, expanded, toggle } = useFold();
  const boxRef = useRef<HTMLTextAreaElement>(null);

  /** Only a change is sent. `closing`: the task is closing, so nothing here can show a banner. */
  const save = async (closing = false) => {
    if (!draft) return;
    if (stale) {
      if (closing) toast.error("Your spec edit wasn't saved", { description: `${stale.changedBy ?? 'Someone'} changed it first.` });
      return;
    }
    const sent = draft;
    const spec = sent.text.trim();
    setDraft(null);
    if (spec === task.spec.trim()) return;
    const prior = lastSave.current;
    const sending = (async () => {
      const before = prior ? await prior : null;
      const base = before?.to && before.from === sent.base ? before.to : sent.base;
      const res = await saveSpec({ spec, ...(base ? { expected_spec_updated_at: base } : {}) }, { spec });
      return { res, base };
    })();
    const outcome = sending.then(
      ({ res, base }) => ({ from: base, to: res.error ? null : (res.data?.spec_updated_at ?? null) }),
      () => ({ from: sent.base, to: null })
    );
    lastSave.current = outcome;
    const { res, base } = await sending;
    if (lastSave.current === outcome) lastSave.current = null;
    if (!res.error) {
      const saved = res.data?.spec_updated_at;
      if (saved) {
        setOwnVersion(saved);
        // Same for an edit still open now.
        setDraft((d) => (d && d.base === base ? { ...d, base: saved } : d));
      }
      return;
    }
    // The change is undone; give the text back rather than lose it.
    setDraft(sent);
    const change = staleChangeOf(res);
    if (change) setStale(change);
    if (closing || !change) toast.error("Your spec edit wasn't saved", { description: res.error });
  };

  // The task closing mid-edit (before focus leaves the box) still saves what was typed.
  const saveOnClose = useEffectEvent(() => {
    if (draft) void save(true);
  });
  useEffect(() => () => saveOnClose(), []);

  const startEditing = () => {
    if (!canEdit || reloading) return;
    setDraft({ text: task.spec, base: hydrated ? version : null });
  };

  /** Their version replaces the draft (the banner says so). A failed reload keeps both. */
  const reload = async () => {
    setReloading(true);
    const loaded = await onReload();
    setReloading(false);
    if (!loaded) {
      toast.error("Couldn't reload the spec", { description: 'Your draft is still here. Try Reload again.' });
      return;
    }
    setDraft(null);
    setStale(null);
  };

  const discard = () => {
    setDraft(null);
    setStale(null);
  };

  // Esc discards. The task pop-up (a Radix dialog) hears Escape on the document before the box does,
  // so it's caught earlier, on the window, as MentionTextarea does.
  const onEscape = useEffectEvent(discard);
  const editing = draft !== null;
  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.target !== boxRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      onEscape();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [editing]);

  if (!draft && !task.spec.trim()) {
    // Nothing to show until it has loaded, or for someone who can't add one.
    if (!hydrated || !canEdit) return null;
    return (
      <div>
        <Button type="button" variant="ghost" size="xs" className="text-muted-foreground max-md:h-11" onClick={startEditing}>
          <Plus className="size-3.5" />
          Add a spec
        </Button>
      </div>
    );
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-1">
        <h3 className="text-sm font-semibold">Spec</h3>
        <InfoTooltip label="the spec">
          The expanded spec a feedback run writes, kept beside the description: the run never changes the task's title or
          description. You can edit it too.
        </InfoTooltip>
      </div>
      {stale ? (
        <DataChangedBanner
          changedBy={stale.changedBy}
          what="the spec"
          hasLocalEdits={draft !== null}
          onReload={() => void reload()}
          reloading={reloading}
          onDismiss={() => setStale(null)}
        />
      ) : null}
      {draft ? (
        <div className="space-y-2">
          <Textarea
            ref={boxRef}
            aria-label="Spec"
            value={draft.text}
            maxLength={TASK_SPEC_MAX}
            onChange={(e) => {
              const text = e.target.value;
              setDraft((d) => (d ? { ...d, text } : d));
            }}
            rows={8}
            // Phones keep the box's 16px text, so the browser doesn't zoom in on it.
            className="max-h-[60dvh] font-mono md:text-xs"
            autoFocus
            placeholder="What to build and why, how it should behave, what's in and out of scope… (Markdown)"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void save();
              }
            }}
            onBlur={() => {
              // Switching to another app or tab isn't leaving the box; it stays open for when you're back.
              if (document.hasFocus()) void save();
            }}
          />
          <p className="text-xs text-muted-foreground">Markdown · Saves when you click away · Esc to discard</p>
        </div>
      ) : (
        // Press to edit. Links inside still open, and selecting text to copy doesn't edit.
        <div
          role={canEdit ? 'button' : undefined}
          tabIndex={canEdit ? 0 : undefined}
          aria-label={canEdit ? 'Edit spec' : undefined}
          className={cn(
            'rounded-md border border-input/60 px-3 py-2 text-sm leading-relaxed transition-colors focus-visible:border-ring focus-visible:outline-none',
            canEdit ? 'cursor-text hover:border-input' : 'cursor-default'
          )}
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('a') || window.getSelection()?.toString()) return;
            startEditing();
          }}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
            e.preventDefault();
            startEditing();
          }}
        >
          <div ref={foldRef} {...foldProps}>
            {/* SF-T-160: its links open in dialogs, as the task's comments do. */}
            <LinksOpenInDialogs>
              <MentionText text={task.spec} members={members} contexts={task.contexts} onNavigate={onNavigate} markdown />
            </LinksOpenInDialogs>
          </div>
        </div>
      )}
      {!draft && foldable ? (
        <Button type="button" variant="outline" size="sm" className="w-full text-muted-foreground" onClick={toggle}>
          {expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      ) : null}
    </section>
  );
}
